import { type AcquireBookErrorType } from "../types";

export interface AcquireBookErrorBadge {
  type: AcquireBookErrorType;
  label: string;
  color: {
    text: string;
  };
}

type ErrorKeywordMatcher = (value: string) => boolean;

interface ErrorMatcher {
  type: AcquireBookErrorType;
  matcher: ErrorKeywordMatcher;
}

export const ERROR_DEFINITIONS: Record<
  AcquireBookErrorType,
  AcquireBookErrorBadge
> = {
  SYSTEM_ERROR: {
    type: "SYSTEM_ERROR",
    label: "システムエラー",
    color: {
      text: "#F172A3",
    },
  },
  NO_RECORD_CONFIRMED: {
    type: "NO_RECORD_CONFIRMED",
    label: "該当なし",
    color: {
      text: "#BF360C",
    },
  },
  NO_RECORD: {
    type: "NO_RECORD",
    label: "該当なしまたはエラー",
    color: {
      text: "#C62828",
    },
  },
  LOCATION: {
    type: "LOCATION",
    label: "所在（地番家屋番号）エラー",
    color: {
      text: "#C62828",
    },
  },
  REGISTRATION_IN_PROGRESS: {
    type: "REGISTRATION_IN_PROGRESS",
    label: "登記事件中",
    color: {
      text: "#F57F17",
    },
  },
  OWNERSHIP: {
    type: "OWNERSHIP",
    label: "所有権エラー",
    color: {
      text: "#C62828",
    },
  },
  GENERIC_FAILED: {
    type: "GENERIC_FAILED",
    label: "失敗",
    color: {
      text: "#C62828",
    },
  },
};

export const ERROR_TYPE_OPTIONS: AcquireBookErrorType[] = [
  "SYSTEM_ERROR",
  "NO_RECORD_CONFIRMED",
  "NO_RECORD",
  "LOCATION",
  "REGISTRATION_IN_PROGRESS",
  "OWNERSHIP",
  "GENERIC_FAILED",
];

export const ERROR_TYPE_LABELS: Record<AcquireBookErrorType, string> =
  Object.fromEntries(
    ERROR_TYPE_OPTIONS.map((type) => [type, ERROR_DEFINITIONS[type].label])
  ) as Record<AcquireBookErrorType, string>;

const contains = (value: string, keyword: string): boolean => {
  return value.includes(keyword);
};

const errorMatchers: ErrorMatcher[] = [
  {
    type: "NO_RECORD",
    matcher: (value) => contains(value, "該当なしまたはエラーの可能性"),
  },
  {
    type: "LOCATION",
    matcher: (value) =>
      contains(value, "不動産一覧に遷移") ||
      contains(value, "地番又は家屋番号"),
  },
  {
    type: "REGISTRATION_IN_PROGRESS",
    matcher: (value) => contains(value, "登記事件"),
  },
  {
    type: "OWNERSHIP",
    matcher: (value) => contains(value, "所有権"),
  },
];

const normalizeDetail = (detail: string | null): string => {
  return (detail ?? "").replace(/\s|\u3000/g, "");
};

const CONFIRMED_EMPTY_DETAIL = "なし（エラーコード:なし、受付番号:なし）";

/**
 * errorCode が「該当なし」のときの errorDetail（空白除去済み）からバッジを決める。
 *
 * - `なし（エラーコード:なし、受付番号:なし）` … BE 確定分岐の compose 結果 → 「該当なし」
 * - 「請求された登記情報が存在しません」を含む … warriors 由来の確定該当なし → 「該当なし」
 * - 「該当なしまたはエラーの可能性」を含む … 曖昧ケース → 「該当なしまたはエラー」
 * - 上記以外 … null（呼び出し側で既存の errorMatchers に委譲）
 */
const classifyNotFoundByDetail = (
  normalizedDetail: string
): AcquireBookErrorBadge | null => {
  if (
    normalizedDetail === CONFIRMED_EMPTY_DETAIL ||
    contains(normalizedDetail, CONFIRMED_EMPTY_DETAIL)
  ) {
    return ERROR_DEFINITIONS.NO_RECORD_CONFIRMED;
  }

  if (contains(normalizedDetail, "請求された登記情報が存在しません")) {
    return ERROR_DEFINITIONS.NO_RECORD_CONFIRMED;
  }

  if (contains(normalizedDetail, "該当なしまたはエラーの可能性")) {
    return ERROR_DEFINITIONS.NO_RECORD;
  }

  return null;
};

export const classifyErrorBadge = (
  errorDetail: string | null,
  errorCode: string | null
): AcquireBookErrorBadge | null => {
  const normalizedDetail = normalizeDetail(errorDetail);

  if (!normalizedDetail && !errorCode) {
    return null;
  }

  if (errorCode === "500") {
    return ERROR_DEFINITIONS.SYSTEM_ERROR;
  }

  if (errorCode === "該当なし") {
    const notFoundBadge = classifyNotFoundByDetail(normalizedDetail);
    if (notFoundBadge) {
      return notFoundBadge;
    }
  }

  for (const matcher of errorMatchers) {
    if (matcher.matcher(normalizedDetail)) {
      return ERROR_DEFINITIONS[matcher.type];
    }
  }

  if (normalizedDetail || errorCode) {
    return ERROR_DEFINITIONS.GENERIC_FAILED;
  }

  // 型推論上ここに来ないはずだが、TypeScript が全経路を推論できないため明示的に null を返す
  return null;
};
